/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import {
    arrow,
    autoUpdate,
    flip,
    type Middleware,
    type MiddlewareState,
    type OffsetOptions,
    offset,
    type Placement,
    shift,
    size,
    type UseFloatingOptions,
    useFloating,
    type VirtualElement,
} from '@floating-ui/react';
import { getScrollParent, useEvent } from '@react-aria/utils';
import clsx from 'clsx';
import * as React from 'react';
import { useObjectRef } from 'react-aria';
import { type ContextValue, useContextProps } from 'react-aria-components';

import { useLiveRef, usePrevious } from '@gitbook/util-react-hooks';
import { assert } from '@gitbook/util-ts';

import { type AnimatedState, AnimatedStateContext } from '../animations';
import { Backdrop } from '../Backdrop';
import {
    Overlay,
    OverlayContext,
    type OverlayProps,
    useOverlayClose,
    useOverlayTriggerRef,
} from '../Overlay';
import { Portal } from '../Portal';
import { FloatingContext } from './Floating';
import { sticky as stickyMiddleware } from './middleware/sticky';
import { PopoverArrowContext } from './PopoverArrow';

export {
    OverlayRoot as PopoverRoot,
    OverlayTrigger as PopoverTrigger,
    useOverlayClose as usePopoverClose,
    useOverlayTriggerRef as usePopoverTriggerRef,
    useOverlayTriggerState as usePopoverTriggerState,
} from '../Overlay';

/**
 * Duration of the animation of the popover.
 */
export const POPOVER_ANIMATION_DURATION_MS = 150;

export type PopoverProps = PopoverOverlayProps & {
    /**
     * Called when the popover is completely hidden.
     */
    onHidden?: () => void;
};

export const PopoverContext = React.createContext<
    ContextValue<Partial<PopoverProps>, HTMLDivElement>
>({});

type PopoverReferenceElement = Element | VirtualElement;

type PopoverReference =
    | PopoverReferenceElement
    | React.RefObject<PopoverReferenceElement | null>
    | (() => PopoverReferenceElement | null);

type ResolvedPopoverOffset = {
    alignmentAxis?: number | null;
    crossAxis?: number;
    mainAxis?: number;
};

type StablePopoverPositionState = {
    verticalEdge: 'top' | 'bottom';
    x: number;
    y: number;
};

const PENDING_STABLE_POSITION_SELECTOR =
    '[data-spine-popover-stable-position-pending],[data-spine-menu][data-ready=false]';

type ReplaceTriggerTransitionStyle = React.CSSProperties & {
    '--sp-popover-scale-x'?: string;
    '--sp-popover-scale-y'?: string;
    '--sp-popover-transform-origin'?: string;
    '--sp-popover-animation-play-state'?: 'paused' | 'running';
};

type AnchorMode = 'outside' | 'overlap';

/**
 * Input passed to a custom popover transition style resolver.
 */
export type PopoverTransitionStyleInput = {
    placement: Placement;
    reference: Element | VirtualElement | null;
    floatingElement: HTMLDivElement | null;
};

/**
 * Custom animation configuration for a popover surface.
 */
export type PopoverCustomTransition = {
    enteringClassName?: string;
    exitingClassName?: string;
    getStyle?: (input: PopoverTransitionStyleInput) => React.CSSProperties | undefined;
};

/**
 * Transition configuration applied to a popover surface.
 */
export type PopoverTransition = 'default' | PopoverCustomTransition;

const DEFAULT_POPOVER_TRANSITION: PopoverCustomTransition = {
    enteringClassName: clsx(
        'animate-in fade-in',
        'data-[side=top]:slide-in-from-bottom-1',
        'data-[side=bottom]:slide-in-from-top-1',
        'data-[side=left]:slide-in-from-right-1',
        'data-[side=right]:slide-in-from-left-1'
    ),
    exitingClassName: clsx(
        'animate-out fade-out',
        'data-[side=top]:slide-out-to-bottom-1',
        'data-[side=bottom]:slide-out-to-top-1',
        'data-[side=left]:slide-out-to-right-1',
        'data-[side=right]:slide-out-to-left-1'
    ),
};

/**
 * Transition that scales a popover from its trigger while keeping Popover in control of lifecycle and positioning.
 */
export const replaceTriggerPopoverTransition: PopoverCustomTransition = {
    enteringClassName: clsx(
        'animate-popover-in fade-in',
        'grow-in-x-[var(--sp-popover-scale-x)]',
        'grow-in-y-[var(--sp-popover-scale-y)]',
        '[animation-play-state:var(--sp-popover-animation-play-state,running)]',
        '[transform-origin:var(--sp-popover-transform-origin,center)]',
        '[will-change:opacity,transform]'
    ),
    exitingClassName: clsx(
        'animate-popover-out fade-out',
        'shrink-out-x-[var(--sp-popover-scale-x)]',
        'shrink-out-y-[var(--sp-popover-scale-y)]',
        '[animation-play-state:var(--sp-popover-animation-play-state,running)]',
        '[transform-origin:var(--sp-popover-transform-origin,center)]',
        '[will-change:opacity,transform]'
    ),
    getStyle: getReplaceTriggerTransitionStyle,
};

/**
 * Popover component to display content in a popover relative to a trigger.
 * Must be used within a PopoverRoot and PopoverTrigger.
 */
export function Popover({
    ref,
    ...props
}: PopoverProps & { ref?: React.ForwardedRef<HTMLDivElement> }) {
    [props, ref] = useContextProps(props, ref, PopoverContext);
    const { onHidden, ...rest } = props;
    const animatedState = React.useContext(AnimatedStateContext);
    const context = React.useContext(OverlayContext);
    assert(context !== null && animatedState !== null, 'Popover must be used within a PopoverRoot');

    const onHiddenRef = useLiveRef(onHidden);
    const isExited = animatedState === 'exited';
    const previousIsExited = usePrevious(isExited);

    React.useEffect(() => {
        if (previousIsExited === false && isExited) {
            onHiddenRef.current?.();
        }
    }, [isExited, previousIsExited, onHiddenRef]);

    if (animatedState === 'exited') {
        return null;
    }

    return <PopoverOverlay ref={ref} {...rest} />;
}

type PopoverOverlayProps = {
    children: React.ReactNode;

    /**
     * Allow to wrap the children.
     * Mainly used with `PopoverContext`.
     */
    wrap?: (children: React.ReactNode) => React.ReactNode;

    /**
     * Placement of the popover relative to the trigger.
     */
    placement?: Placement;

    /**
     * Explicit ordered list of fallback placements for the `flip` middleware.
     * When provided, it overrides the default `fallbackAxisSideDirection` behaviour
     * and tries each placement in order before giving up.
     */
    fallbackPlacements?: Placement[];

    /**
     * Minimum gap (in pixels) to keep between the popover and the clipping
     * boundary (typically the viewport). Threaded into the `padding` option of
     * `size`, `flip`, `shift`, and the custom sticky middleware. Defaults to a
     * value derived from `offset`.
     */
    viewportPadding?: number;

    /**
     * Distance in pixels between the trigger and popover.
     * Accepts any Floating UI offset option.
     * @default 8
     */
    offset?: OffsetOptions;

    /**
     * Position the popover either outside the trigger or overlapping it.
     * @default "outside"
     */
    anchorMode?: AnchorMode;

    /**
     * Transition configuration used by the popover surface.
     * @default "default"
     */
    transition?: PopoverTransition;

    /**
     * Reposition the popover relative to the anchor when the parent is scrolled.
     * Also disables the scroll-to-dismiss behaviour of popovers.
     * @default false
     */
    sticky?: { boundary?: Element } | true | false;

    /**
     * Whether to display a backdrop behind the popover
     * @default false
     */
    backdrop?: boolean | React.ReactNode;

    /**
     * Disable animations.
     * @default true
     */
    animate?: boolean | undefined;

    /**
     * Whether the popover should render in a Portal.
     * @default true
     */
    rendersInPortal?: boolean;

    /**
     * Keep the first computed position while the popover is mounted.
     * Useful for searchable menus whose result set changes while the user types.
     * @default false
     */
    stablePosition?: boolean;

    /**
     * Classes to apply to the Popover's surface
     */
    className?: string;

    /**
     * Floating options.
     */
    floating?: Pick<UseFloatingOptions, 'middleware'> & {
        /**
         * Override the reference used for positioning.
         * Defaults to the popover trigger.
         */
        reference?: PopoverReference;
    };

    /**
     * Constrain the popover's dimensions to the available space via Floating UI's
     * `size` middleware. When set, the popover shrinks within the given bounds
     * before flipping to another side. The available space is also exposed as
     * `--sp-popover-available-width` / `--sp-popover-available-height` CSS custom
     * properties on the floating surface.
     */
    resize?: {
        minWidth?: number;
        maxWidth?: number;
        minHeight?: number;
        maxHeight?: number;
    };

    /**
     * Border radius of the popover.
     */
    rounded?: 'md' | 'xl' | '2xl' | '3xl';
} & OverlayProps;

const PopoverOverlay = function PopoverOverlay({
    ref,
    ...props
}: PopoverOverlayProps & { ref?: React.ForwardedRef<HTMLDivElement> }) {
    const triggerRef = useOverlayTriggerRef();
    const overlayRef = useObjectRef(ref);
    const arrowRef = React.useRef<SVGSVGElement>(null);
    const closeOverlay = useOverlayClose();
    const {
        placement = 'bottom',
        fallbackPlacements,
        viewportPadding,
        children,
        sticky = false,
        backdrop = false,
        animate = true,
        rendersInPortal = true,
        stablePosition = false,
        anchorMode = 'outside',
        floating,
        className,
        offset: offsetValue = anchorMode === 'overlap' ? 0 : 8,
        style: styleProp,
        transition = 'default',
        rounded = 'md',
        resize,
        wrap = (x) => x,
        ...rest
    } = props;

    const positioningReference = resolvePopoverReference(floating?.reference, triggerRef.current);
    const referenceContextElement = getPopoverReferenceContextElement(positioningReference);
    const padding = viewportPadding ?? getPopoverPadding(offsetValue);
    const overlapMiddleware: Middleware[] =
        anchorMode === 'overlap' ? [offset((state) => getOverlapOffset(state))] : [];
    const resizeMiddleware: Middleware[] = resize
        ? [
              size({
                  padding,
                  apply: ({ availableWidth, availableHeight, elements }) => {
                      const { minWidth, maxWidth, minHeight, maxHeight } = resize;
                      const styles = elements.floating.style;
                      // Apply min/max constraints rather than forcing width/height,
                      // so the popover keeps its intrinsic size when only one bound is provided.
                      // The `maxWidth`/`maxHeight` cap only kicks in when there's at least
                      // `minWidth`/`minHeight` of room — otherwise the popover would shrink
                      // below its declared minimum, fit at the current placement, and rob
                      // `flip` of the overflow signal it needs to promote a fallback.
                      if (minWidth != null) {
                          styles.minWidth = `${minWidth}px`;
                      }
                      if (maxWidth != null) {
                          const cap =
                              minWidth != null && availableWidth < minWidth
                                  ? maxWidth
                                  : Math.min(maxWidth, availableWidth);
                          styles.maxWidth = `${cap}px`;
                      }
                      if (minHeight != null) {
                          styles.minHeight = `${minHeight}px`;
                      }
                      if (maxHeight != null) {
                          const cap =
                              minHeight != null && availableHeight < minHeight
                                  ? maxHeight
                                  : Math.min(maxHeight, availableHeight);
                          styles.maxHeight = `${cap}px`;
                      }
                      styles.setProperty(
                          '--sp-popover-available-width',
                          `${Math.max(0, availableWidth)}px`
                      );
                      styles.setProperty(
                          '--sp-popover-available-height',
                          `${Math.max(0, availableHeight)}px`
                      );
                  },
              }),
          ]
        : [];

    const scrollParent = referenceContextElement ? getScrollParent(referenceContextElement) : null;
    const scrollParentRef = useLiveRef(scrollParent);

    const {
        x,
        y,
        isPositioned,
        update,
        placement: currentPlacement,
        refs,
        strategy,
        floatingStyles,
        context,
    } = useFloating({
        open: stablePosition ? true : undefined,
        transform: false,
        placement,
        whileElementsMounted: autoUpdate,
        middleware: floating?.middleware
            ? [...overlapMiddleware, ...floating.middleware]
            : [
                  // `offset` runs before `size` so that `size`'s availableWidth /
                  // availableHeight are computed against the offset-adjusted
                  // position — otherwise the mainAxis offset eats into the
                  // viewport padding when the popover is placed on the side.
                  // `size` then runs before `flip` so the popover shrinks to fit
                  // the available space first; `flip` evaluates overflow against
                  // the shrunken size and only flips when shrinking isn't enough.
                  offset((state) =>
                      mergePopoverOffsets(
                          anchorMode === 'overlap' ? getOverlapOffset(state) : undefined,
                          resolvePopoverOffset(offsetValue, state)
                      )
                  ),
                  ...resizeMiddleware,
                  flip({
                      fallbackAxisSideDirection: 'end',
                      fallbackPlacements,
                      padding,
                      // Don't flip on axis nor alignment when a popover is sticky, as this would make it 'jump' right before the anchor scrolls out of the viewport.
                      crossAxis: sticky ? false : placement.includes('-'),
                      flipAlignment: sticky ? false : undefined,
                  }),
                  stickyMiddleware({
                      enabled: Boolean(sticky),
                      onDismiss: closeOverlay,
                      padding,
                      boundary: typeof sticky === 'object' ? sticky.boundary : undefined,
                  }),
                  shift({ padding }),
                  arrow({ element: arrowRef }),
              ],
    });

    React.useLayoutEffect(() => {
        refs.setReference(positioningReference);
        refs.setFloating(overlayRef.current);
    });

    const animatedState = React.useContext(AnimatedStateContext);
    const isExiting = animatedState === 'exiting';
    const isEntering = animatedState === 'entering';
    const resolvedTransition = resolvePopoverTransition(transition);

    const [transitionStyle, setTransitionStyle] = React.useState<React.CSSProperties>();
    const [stablePositionState, setStablePositionState] =
        React.useState<StablePopoverPositionState | null>(null);
    const latestPositionRef = useLiveRef({ isPositioned, x, y });

    React.useLayoutEffect(() => {
        if (!resolvedTransition.getStyle) {
            setTransitionStyle(undefined);
            return;
        }

        setTransitionStyle(
            resolvedTransition.getStyle({
                placement: currentPlacement,
                reference: positioningReference,
                floatingElement: overlayRef.current,
            })
        );
    }, [currentPlacement, overlayRef, positioningReference, resolvedTransition]);

    // Capture the stable position once. Gated to its position/readiness inputs so it
    // doesn't re-run — and re-schedule the measure/reposition work — on every unrelated
    // re-render while the popover is still settling. `autoUpdate` bumps `x`/`y` when the
    // content resizes, so a pending menu still triggers a capture once it's ready.
    React.useLayoutEffect(() => {
        if (!stablePosition) {
            if (stablePositionState) {
                setStablePositionState(null);
            }
            return;
        }

        const overlayElement = overlayRef.current;
        if (stablePositionState || !isPositioned || x == null || y == null || !overlayElement) {
            return;
        }

        const window = overlayElement.ownerDocument.defaultView;
        if (!window) {
            return;
        }

        if (overlayElement.querySelector(PENDING_STABLE_POSITION_SELECTOR)) {
            return;
        }

        let captureFrame = 0;
        const updateFrame = window.requestAnimationFrame(() => {
            update();
            captureFrame = window.requestAnimationFrame(() => {
                const latestPosition = latestPositionRef.current;
                if (
                    latestPosition.isPositioned &&
                    latestPosition.x != null &&
                    latestPosition.y != null
                ) {
                    const floatingRect = overlayElement.getBoundingClientRect();
                    const verticalEdge = getStablePopoverVerticalEdge({
                        floatingRect,
                        referenceRect: positioningReference?.getBoundingClientRect() ?? null,
                    });
                    setStablePositionState({
                        verticalEdge,
                        x: latestPosition.x,
                        y:
                            verticalEdge === 'bottom'
                                ? latestPosition.y + floatingRect.height
                                : latestPosition.y,
                    });
                }
            });
        });

        return () => {
            window.cancelAnimationFrame(updateFrame);
            if (captureFrame) {
                window.cancelAnimationFrame(captureFrame);
            }
        };
    }, [
        stablePosition,
        stablePositionState,
        overlayRef,
        isPositioned,
        x,
        y,
        update,
        latestPositionRef,
        positioningReference,
    ]);

    React.useEffect(() => {
        if (!stablePosition) {
            return;
        }

        const overlayElement = overlayRef.current;
        const window = overlayElement?.ownerDocument.defaultView;
        if (!overlayElement || !window) {
            return;
        }

        const recomputeStablePosition = () => {
            setStablePositionState(null);
            update();
        };

        window.addEventListener('resize', recomputeStablePosition);

        // The stable position is frozen once, at the size the popover had when it
        // first settled — which is often while an async menu is still empty. When
        // its content then grows (e.g. search results arrive), the frozen position
        // can leave the popover partly off-screen. Recompute only when the popover's
        // own resize pushes it past the viewport, so in-bounds content changes keep
        // their stable position and don't jitter while the user types.
        const resizeObserver = new window.ResizeObserver(() => {
            if (isOverflowingViewport(overlayElement, window, padding)) {
                recomputeStablePosition();
            }
        });
        resizeObserver.observe(overlayElement);

        return () => {
            window.removeEventListener('resize', recomputeStablePosition);
            resizeObserver.disconnect();
        };
    }, [overlayRef, padding, stablePosition, update]);

    // A scroll queued before the popover opened (e.g. the trigger being scrolled into view) fires
    // after the listener attaches: only dismiss when the offset actually moved since opening.
    const openScrollOffsetRef = React.useRef({ left: 0, top: 0 });
    React.useLayoutEffect(() => {
        openScrollOffsetRef.current = {
            left: scrollParent?.scrollLeft ?? 0,
            top: scrollParent?.scrollTop ?? 0,
        };
    }, [scrollParent]);

    const closeOnScroll = () => {
        const { left, top } = openScrollOffsetRef.current;
        const element = scrollParentRef.current;
        if (element && element.scrollLeft === left && element.scrollTop === top) {
            return;
        }
        closeOverlay();
    };

    useEvent(scrollParentRef, 'scroll', sticky ? update : closeOnScroll, { passive: true });

    // Extract side (top, bottom, left, right)
    const side = currentPlacement.split('-')[0];
    const portalContainer = referenceContextElement
        ? (findClosestPortalContainer(referenceContextElement) ?? undefined)
        : undefined;
    const overlayStyle: ReplaceTriggerTransitionStyle = {
        ...floatingStyles,
        ...transitionStyle,
        ...styleProp,
        ...(stablePositionState
            ? {
                  left: stablePositionState.x,
                  position: strategy,
                  top: stablePositionState.y,
                  translate: stablePositionState.verticalEdge === 'bottom' ? '0 -100%' : undefined,
              }
            : {}),
    };
    const content = (
        <FloatingContext.Provider value={context}>
            {backdrop === true ? (
                <Backdrop isExiting={isExiting} isEntering={isEntering} />
            ) : (
                backdrop
            )}
            <Overlay
                ref={overlayRef}
                data-spine-popover=""
                data-anchor-mode={anchorMode}
                className={clsx(
                    'z-overlay fill-mode-forwards',
                    'overflow-hidden bg-base shadow-overlay transition-none dark:border dark:border-neutral-subtle',
                    {
                        'rounded-md': rounded === 'md',
                        'rounded-xl': rounded === 'xl',
                        'rounded-2xl': rounded === '2xl',
                        'rounded-3xl': rounded === '3xl',
                    },
                    animate && getAnimationClasses(resolvedTransition, animatedState),
                    className
                )}
                data-side={side}
                style={overlayStyle}
                {...rest}
            >
                <PopoverArrowContext.Provider value={{ ref: arrowRef, context }}>
                    {wrap(children)}
                </PopoverArrowContext.Provider>
            </Overlay>
        </FloatingContext.Provider>
    );

    if (!rendersInPortal) {
        return content;
    }

    return <Portal container={portalContainer}>{content}</Portal>;
};

/**
 * Find the closest popover element.
 */
export function findClosestPopoverElement(element: Element): Element | null {
    return element.closest('[data-spine-popover]');
}

/**
 * Find the closest candidate to be the portal container.
 */
function findClosestPortalContainer(element: Element): Element | null {
    return element.closest('[data-spine-modal],[data-spine-sidebar]');
}

function resolvePopoverReference(
    reference: PopoverReference | undefined,
    fallbackReference: HTMLElement | null
): PopoverReferenceElement | null {
    if (!reference) {
        return fallbackReference;
    }

    if (typeof reference === 'function') {
        return reference() ?? fallbackReference;
    }

    if (isPopoverReferenceRef(reference)) {
        return reference.current ?? fallbackReference;
    }

    return reference ?? fallbackReference;
}

function isPopoverReferenceRef(
    reference: PopoverReference
): reference is React.RefObject<PopoverReferenceElement | null> {
    return typeof reference === 'object' && reference !== null && 'current' in reference;
}

function getPopoverReferenceContextElement(
    reference: PopoverReferenceElement | null
): Element | null {
    if (!reference) {
        return null;
    }

    if (reference instanceof Element) {
        return reference;
    }

    return reference.contextElement ?? null;
}

function getStablePopoverVerticalEdge(props: {
    floatingRect: Pick<DOMRect, 'bottom' | 'top'>;
    referenceRect: Pick<DOMRect, 'bottom' | 'top'> | null;
}): StablePopoverPositionState['verticalEdge'] {
    if (!props.referenceRect) {
        return 'top';
    }

    const topDistance = getClosestEdgeDistance(props.floatingRect.top, props.referenceRect);
    const bottomDistance = getClosestEdgeDistance(props.floatingRect.bottom, props.referenceRect);

    return bottomDistance < topDistance ? 'bottom' : 'top';
}

function getClosestEdgeDistance(edge: number, referenceRect: Pick<DOMRect, 'bottom' | 'top'>) {
    return Math.min(Math.abs(edge - referenceRect.top), Math.abs(edge - referenceRect.bottom));
}

/**
 * Whether a floating element extends past the viewport edges (inset by `padding`).
 * Used to detect when a frozen stable position has gone stale because the popover's
 * content changed size after the position was captured.
 */
function isOverflowingViewport(element: Element, window: Window, padding: number): boolean {
    const rect = element.getBoundingClientRect();
    return (
        rect.top < padding ||
        rect.left < padding ||
        rect.bottom > window.innerHeight - padding ||
        rect.right > window.innerWidth - padding
    );
}

function getPopoverPadding(offsetValue: OffsetOptions) {
    if (typeof offsetValue === 'function') {
        return 8;
    }

    if (typeof offsetValue === 'number') {
        return Math.max(8, Math.abs(offsetValue));
    }

    if (!offsetValue) {
        return 8;
    }

    return Math.max(
        8,
        Math.abs(offsetValue.mainAxis ?? 0),
        Math.abs(offsetValue.crossAxis ?? 0),
        Math.abs(offsetValue.alignmentAxis ?? 0)
    );
}

function resolvePopoverOffset(
    offsetValue: OffsetOptions,
    state: MiddlewareState
): number | ResolvedPopoverOffset | undefined {
    const resolvedOffset = typeof offsetValue === 'function' ? offsetValue(state) : offsetValue;

    if (typeof resolvedOffset === 'number') {
        return resolvedOffset;
    }

    if (!resolvedOffset) {
        return undefined;
    }

    return {
        alignmentAxis: resolvedOffset.alignmentAxis,
        crossAxis: resolvedOffset.crossAxis,
        mainAxis: resolvedOffset.mainAxis,
    };
}

function getOverlapOffset(state: MiddlewareState) {
    const side = state.placement.split('-')[0];
    return {
        mainAxis:
            side === 'top' || side === 'bottom'
                ? -state.rects.reference.height
                : -state.rects.reference.width,
    };
}

function mergePopoverOffsets(
    baseOffset: number | ResolvedPopoverOffset | undefined,
    userOffset: number | ResolvedPopoverOffset | undefined
) {
    const base = normalizePopoverOffset(baseOffset);
    const user = normalizePopoverOffset(userOffset);

    return {
        mainAxis: (base.mainAxis ?? 0) + (user.mainAxis ?? 0),
        crossAxis: (base.crossAxis ?? 0) + (user.crossAxis ?? 0),
        ...(base.alignmentAxis != null || user.alignmentAxis != null
            ? {
                  alignmentAxis: (base.alignmentAxis ?? 0) + (user.alignmentAxis ?? 0),
              }
            : {}),
    };
}

function normalizePopoverOffset(offsetValue: number | ResolvedPopoverOffset | undefined) {
    if (typeof offsetValue === 'number') {
        return { mainAxis: offsetValue };
    }

    return offsetValue ?? {};
}

function resolvePopoverTransition(transition: PopoverTransition): PopoverCustomTransition {
    return transition === 'default' ? DEFAULT_POPOVER_TRANSITION : transition;
}

function getReplaceTriggerTransitionStyle(
    props: PopoverTransitionStyleInput
): ReplaceTriggerTransitionStyle | undefined {
    const { placement, reference, floatingElement } = props;

    if (!reference || !floatingElement) {
        return undefined;
    }

    const referenceRect = reference.getBoundingClientRect();
    const floatingRect = floatingElement.getBoundingClientRect();
    if (!floatingRect.width || !floatingRect.height) {
        return undefined;
    }

    const scaleX = clampPopoverScale(referenceRect.width / floatingRect.width);
    const scaleY = clampPopoverScale(referenceRect.height / floatingRect.height);

    return {
        '--sp-popover-scale-x': `${scaleX}`,
        '--sp-popover-scale-y': `${scaleY}`,
        '--sp-popover-transform-origin': getPopoverTransformOrigin(placement),
    };
}

function clampPopoverScale(value: number) {
    return Math.min(1, Math.max(0.1, value));
}

function getPopoverTransformOrigin(placement: Placement) {
    const [side, alignment] = placement.split('-') as [string, string | undefined];

    const crossAxis = alignment === 'start' ? 'left' : alignment === 'end' ? 'right' : 'center';

    switch (side) {
        case 'top':
            return `bottom ${crossAxis}`;
        case 'bottom':
            return `top ${crossAxis}`;
        case 'left':
            return `${alignment === 'end' ? 'bottom' : alignment === 'start' ? 'top' : 'center'} right`;
        case 'right':
            return `${alignment === 'end' ? 'bottom' : alignment === 'start' ? 'top' : 'center'} left`;
        default:
            return 'center';
    }
}

function getAnimationClasses(
    transition: PopoverCustomTransition,
    state: AnimatedState | null
): string | undefined {
    if (state === 'entering') {
        return transition.enteringClassName;
    }

    if (state === 'exiting') {
        return transition.exitingClassName;
    }

    return undefined;
}
